// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useInView, useAnimation, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatRating, formatReviewCount } from '../../utils/rating';
import { formatTrainingLocation, formatTrainingCenterLocation } from '../../utils/address';
import { 
  ArrowLeft,
  Star,
  Clock,
  MapPin,
  Users,
  Calendar,
  Award,
  BookOpen,
  Download,
  Share2,
  Heart,
  CheckCircle,
  Building2,
  Globe,
  Target,
  TrendingUp,
  Shield,
  Zap,
  FileText,
  Play,
  Phone,
  Mail,
  ExternalLink,
  ThumbsUp,
  MessageCircle,
  User,
  Filter,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { trainingsService, Training } from '../../services/trainings.service';
import CountdownTimer from '../../components/common/CountdownTimer';
import { useAuth } from '../../context/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import traineeService from '../../services/trainee.service';

interface Review {
  id: number;
  training_id: number;
  user_id: number;
  rating: number;
  comment: string;
  helpful_count: number;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
  };
}

// Animation variants for modern landing page effects
const fadeUpVariants: any = {
  hidden: { 
    opacity: 0, 
    y: 60,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1
  }
};

const staggerContainer: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1
  }
};

const slideInVariants: any = {
  hidden: { 
    opacity: 0, 
    x: -50,
    scale: 0.9
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1
  }
};

const scaleVariants: any = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0
  }
};

const pulseVariants: any = {
  hover: {
    scale: 1.05
  },
  tap: {
    scale: 0.95
  }
};

const floatingVariants: any = {
  animate: {
    y: [-10, 10, -10]
  }
};

// Scroll reveal component
const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0
        }
      }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const TrainingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  
  const [training, setTraining] = useState<Training | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<'all' | '5' | '4' | '3' | '2' | '1'>('all');
  const [selectedRating, setSelectedRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    description: false,
    objectives: false,
    skills: false,
    audience: false
  });
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [formData, setFormData] = useState({
    current_position: '',
    company_name: '',
    department: '',
    years_of_experience: '',
    has_special_accommodation: false,
    special_accommodation_details: ''
  });
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Utility function to format duration for display
  const formatDuration = (duration?: string | number): string => {
    if (!duration) return 'N/A';
    
    // If duration is a string like "3 days", parse it
    if (typeof duration === 'string') {
      const match = duration.match(/(\d+)\s*(hour|day|week|heure|jour|semaine)s?/i);
      if (match) {
        const value = parseInt(match[1]);
        const unit = match[2].toLowerCase();
        
        switch (unit) {
          case 'day':
          case 'jour':
            return `${value} day${value > 1 ? 's' : ''}`;
          case 'week':
          case 'semaine':
            return `${value} week${value > 1 ? 's' : ''}`;
          case 'hour':
          case 'heure':
          default:
            return `${value}h`;
        }
      }
      return duration; // Return as-is if no match
    }
    
    // If duration is a number, assume it's hours
    return `${duration}h`;
  };

  // Check if user is enrolled in this training
  const checkEnrollment = async () => {
    if (!isAuthenticated || !training) return;
    
    try {
      const enrolledTrainings = await traineeService.getMyTrainings();
      const enrolled = enrolledTrainings.some(t => t.id === training.id.toString());
      setIsEnrolled(enrolled);
    } catch (error) {
    }
  };

  // Check if training has any available (non-expired) sessions
  const hasAvailableSessions = () => {
    if (!training || !training.sessions || training.sessions.length === 0) return false;
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    return training.sessions.some(session => {
      const sessionDate = new Date(session.session_date);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate >= now;
    });
  };

  // Check if all sessions are expired
  const areAllSessionsExpired = () => {
    if (!training || !training.sessions || training.sessions.length === 0) return false;
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    // If there are sessions, check if all are expired
    return training.sessions.every(session => {
      const sessionDate = new Date(session.session_date);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate < now;
    });
  };

  // Check if a specific session is expired
  const isSessionExpired = (sessionDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const session = new Date(sessionDate);
    session.setHours(0, 0, 0, 0);
    return session < today;
  };

  useEffect(() => {
    if (id) {
      loadTraining();
    }
    
    // Load subscription info from database to pre-fill form
    if (isAuthenticated) {
      loadSubscriptionInfo();
    }
  }, [id, isAuthenticated]);

  // Check enrollment status when training is loaded
  useEffect(() => {
    if (training && isAuthenticated) {
      checkEnrollment();
      
      // Check if all sessions are expired
      if (!hasAvailableSessions()) {
        setEnrollmentError(t('training.subscriptionsNotAvailable'));
      }
    }
  }, [training, isAuthenticated]);

  const loadSubscriptionInfo = async () => {
    try {
      const subscriptionInfo = await traineeService.getSubscriptionInfo();
      setFormData({
        current_position: subscriptionInfo.current_position || '',
        company_name: subscriptionInfo.company_name || '',
        department: subscriptionInfo.department || '',
        years_of_experience: subscriptionInfo.years_of_experience || '',
        has_special_accommodation: subscriptionInfo.has_special_accommodation || false,
        special_accommodation_details: subscriptionInfo.special_accommodation_details || ''
      });
    } catch (error) {
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadTraining = async () => {
    try {
      setIsLoading(true);
      const data = await trainingsService.getTrainingPublic(id!);
      setTraining(data);
      
      // Track view
      await trainingsService.trackTrainingView(id!);
      
      // Load reviews
      await loadReviews();
    } catch (error) {
      setError(t('view.errors.load'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const reviewsData = await trainingsService.getTrainingReviews(id!);
      setReviews(reviewsData);
    } catch (error) {
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (price?: number) => {
    if (!price) return 'Gratuit';
    return `${price.toLocaleString()} DZD`;
  };

  const handleSubscribe = () => {
    if (!isAuthenticated) {
      // Redirect to trainee login page
      navigate('/auth/user');
      return;
    }
    
    // Check if user is already enrolled
    if (isEnrolled) {
      setEnrollmentError(t('training.alreadyEnrolled'));
      return;
    }
    
    // Check if training has available sessions
    if (!hasAvailableSessions()) {
      setEnrollmentError(t('training.subscriptionsNotAvailable'));
      return;
    }
    
    // Show the subscription form first
    setShowSubscriptionForm(true);
  };

  const handleFormSubmit = () => {
    // Navigate to subscription page with form data
    navigate(`/training-subscription/${training?.id}`, {
      state: { formData }
    });
  };

  const getStatusFromDate = (scheduleDate: string) => {
    // If all sessions are expired, show expired status
    if (areAllSessionsExpired()) {
      return { text: t('training.status.expired'), color: 'bg-red-100 text-red-700 border-red-200' };
    }
    
    if (!scheduleDate) return { text: t('training.status.scheduled'), color: 'bg-green-100 text-green-700 border-green-200' };
    const today = new Date();
    const trainingDate = new Date(scheduleDate);
    if (isNaN(trainingDate.getTime())) return { text: t('training.status.scheduled'), color: 'bg-green-100 text-green-700 border-green-200' };

    if (trainingDate < today) {
      return { text: t('training.status.completed'), color: 'bg-gray-100 text-gray-700 border-gray-200' };
    } else if (trainingDate.getTime() - today.getTime() < 7 * 24 * 60 * 60 * 1000) {
      return { text: t('training.status.ongoing'), color: 'bg-blue-100 text-blue-700 border-blue-200' };
    } else {
      return { text: t('training.status.scheduled'), color: 'bg-green-100 text-green-700 border-green-200' };
    }
  };

  const getAverageRating = () => {
    if (training?.ratingStats) {
      return training.ratingStats.averageRating;
    }
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, review) => sum + review.rating, 0);
    return Math.round((total / reviews.length) * 10) / 10;
  };

  const getRatingDistribution = () => {
    if (training?.ratingStats) {
      return training.ratingStats.ratingDistribution;
    }
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++;
    });
    return distribution;
  };

  const getFilteredReviews = () => {
    if (reviewFilter === 'all') return reviews;
    return reviews.filter(review => review.rating === parseInt(reviewFilter));
  };

  const formatReviewDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSubmitReview = async () => {
    if (selectedRating === 0 || !reviewComment.trim()) {
      alert(t('training.selectRatingAndComment'));
      return;
    }

    try {
      setIsSubmittingReview(true);
      await trainingsService.createReview(id!, {
        rating: selectedRating,
        comment: reviewComment.trim()
      });
      
      // Reload reviews and training data
      await loadReviews();
      await loadTraining();
      
      // Reset form
      setSelectedRating(0);
      setReviewComment('');
      setShowReviewForm(false);
      
      alert(t('training.reviewPublishedSuccess'));
    } catch (error) {
      alert(t('common.error'));
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleMarkHelpful = async (reviewId: number) => {
    try {
      await trainingsService.markReviewHelpful(id!, reviewId);
      await loadReviews(); // Reload to get updated helpful count
    } catch (error) {
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-primary/20"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-primary border-t-transparent absolute top-0 left-0"></div>
          </div>
          <p className="text-gray-600 mt-4 text-lg font-medium">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !training) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="h-12 w-12 text-brand-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('training.notFound.title')}</h2>
          <p className="text-gray-600 mb-8 text-lg">{error || t('training.notFound.subtitle')}</p>
          <div className="flex gap-4 justify-center">
            <motion.button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-brand-primary to-purple-600 text-white px-8 py-3 rounded-xl hover:from-purple-600 hover:to-brand-primary transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              variants={pulseVariants}
              whileHover="hover"
              whileTap="tap"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {t('common.backToHome')}
            </motion.button>
            <motion.button
              onClick={() => navigate('/trainings')}
              className="border-2 border-brand-secondary text-brand-secondary px-8 py-3 rounded-xl hover:bg-brand-secondary hover:text-white transition-all duration-200 font-semibold"
              variants={pulseVariants}
              whileHover="hover"
              whileTap="tap"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {t('training.viewCatalog')}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  const status = getStatusFromDate(training.schedule_date);

  return (
    <div className="min-h-screen bg-brand-surface">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-3 text-gray-600 hover:text-brand-primary transition-colors font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('common.back')}
            </button>
            
            <div className="flex items-center gap-4">
              <button className="p-3 text-gray-600 hover:text-brand-secondary transition-colors hover:bg-brand-secondary/5 rounded-xl">
                <Share2 className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsFavorite(!isFavorite)}
                className={`p-3 transition-all duration-200 rounded-xl ${
                  isFavorite 
                    ? 'text-brand-secondary bg-brand-secondary/10' 
                    : 'text-gray-600 hover:text-brand-secondary hover:bg-brand-secondary/5'
                }`}
              >
                <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Section */}
            <motion.div 
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8"
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                staggerChildren: 0.1
              }}
            >
              {/* Banner Image */}
              <div className="h-80 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 relative overflow-hidden">
                {/* Animated background gradient */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-brand-primary/5 via-transparent to-brand-secondary/5"
                  animate={{
                    background: [
                      "linear-gradient(45deg, rgba(139, 69, 19, 0.05) 0%, rgba(0, 0, 0, 0) 50%, rgba(236, 72, 153, 0.05) 100%)",
                      "linear-gradient(45deg, rgba(236, 72, 153, 0.05) 0%, rgba(0, 0, 0, 0) 50%, rgba(139, 69, 19, 0.05) 100%)",
                      "linear-gradient(45deg, rgba(139, 69, 19, 0.05) 0%, rgba(0, 0, 0, 0) 50%, rgba(236, 72, 153, 0.05) 100%)"
                    ]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                {training.banner_image ? (
                  <motion.img
                    src={`${API_ENDPOINTS.UPLOADS}/${training.banner_image}`}
                    alt={training.title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                    }}
                  />
                ) : (
                  <motion.div 
                    className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <motion.div 
                      className="text-center"
                      variants={floatingVariants}
                      animate="animate"
                      transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <BookOpen className="h-20 w-20 text-brand-primary mx-auto mb-4" />
                      <div className="text-lg font-semibold text-brand-primary">{t('training.pharmaceuticalTraining')}</div>
                    </motion.div>
                  </motion.div>
                )}
                
                {/* Status Badge */}
                <motion.div 
                  className="absolute top-6 left-6"
                  initial={{ opacity: 0, x: -30, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <span className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 shadow-lg ${
                    status.text === t('training.status.expired') ? 'bg-red-100 text-red-700 border-red-200' :
                    status.text === t('training.status.completed') ? 'bg-gray-100 text-gray-700 border-gray-200' :
                    status.text === t('training.status.ongoing') ? 'bg-gradient-to-r from-brand-secondary to-pink-600 text-white border-brand-secondary' :
                    'bg-gradient-to-r from-brand-mint to-teal-600 text-white border-brand-mint'
                  }`}>
                    {status.text}
                  </span>
                </motion.div>

                {/* Rating Badge */}
                <motion.div 
                  className="absolute top-6 right-6"
                  initial={{ opacity: 0, x: 30, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <Star className="h-5 w-5 text-brand-accent fill-current" />
                        <span className="font-bold text-gray-900 text-lg">
                          {formatRating(training.rating)}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600 font-medium">
                        ({formatReviewCount(training.reviews)} {t('training.reviews')})
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-8">
                <motion.div 
                  className="flex items-start justify-between mb-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  transition={{
                    staggerChildren: 0.15,
                    delayChildren: 0.2
                  }}
                >
                  <div className="flex-1">
                    <motion.h1 
                      className="text-4xl font-bold text-gray-900 mb-3 leading-tight"
                      variants={fadeUpVariants}
                    >
                      {training.title}
                    </motion.h1>
                    <motion.div 
                      className="flex items-center gap-3 mb-4"
                      variants={slideInVariants}
                    >
                      <motion.div 
                        className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Building2 className="h-5 w-5 text-white" />
                      </motion.div>
                      <div>
                        <p className="text-xl font-semibold text-gray-800">
                      {training.training_center?.name}
                    </p>
                        <p className="text-gray-600">
                          {formatTrainingCenterLocation(training.training_center)}
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Meta Information */}
                <motion.div 
                  className="mb-8 flex flex-wrap gap-3"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  transition={{
                    staggerChildren: 0.1,
                    delayChildren: 0.3
                  }}
                >
                  {training.format && (
                    <motion.span 
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-secondary to-pink-600 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <BookOpen className="h-4 w-4" />
                      {training.format}
                    </motion.span>
                  )}
                  {training.language && (
                    <motion.span 
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-mint to-teal-600 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Globe className="h-4 w-4" />
                      {training.language}
                    </motion.span>
                  )}
                  {training.reference && (
                    <motion.span 
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-accent to-yellow-600 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FileText className="h-4 w-4" />
                      Ref: {training.reference}
                    </motion.span>
                  )}
                  {training.level && (
                    <motion.span 
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Target className="h-4 w-4" />
                      {t('training.levelLabel')}: {training.level}
                    </motion.span>
                  )}
                </motion.div>

                {/* Sessions with Cool Countdown Design */}
                <div className="mb-6">
                  {training.sessions && training.sessions.length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-brand-primary" />
                        {t('training.availableSessions')} ({training.sessions.length})
                      </h3>
                      {training.sessions
                        .sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime())
                        .map((session, index) => (
                        <div 
                          key={session.id} 
                          className="group relative overflow-hidden bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 hover:border-brand-primary/30"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <div className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                                  <span className="text-sm font-bold text-white">
                                    {index + 1}
                                  </span>
                                </div>
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-brand-accent to-yellow-500 rounded-full flex items-center justify-center">
                                  <Clock className="h-2.5 w-2.5 text-white" />
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 group-hover:text-brand-primary transition-colors">
                                  {t('training.session')} {index + 1}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {new Date(session.session_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500 mb-1 font-medium">{t('training.timeRemaining')}</div>
                              <CountdownTimer 
                                targetDate={session.session_date} 
                                variant="card"
                                className="w-full"
                              />
                            </div>
                          </div>
                          {/* Animated background effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/5 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mb-6">
                      <div className="bg-gradient-to-r from-brand-primary/10 to-purple-50 rounded-xl p-4 sm:p-6 border border-brand-primary/20">
                        <div className="text-center">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-brand-primary to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                          </div>
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{t('training.nextSession')}</h3>
                          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 px-2">
                            {training.schedule_date && !isNaN(new Date(training.schedule_date).getTime())
                              ? new Date(training.schedule_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })
                              : t('training.noSession')}
                          </p>
                          <div className="px-1 sm:px-2 max-w-full">
                            {training.schedule_date && !isNaN(new Date(training.schedule_date).getTime()) && (
                            <CountdownTimer
                              targetDate={training.schedule_date}
                              variant="card"
                              className="w-full mx-auto"
                            />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Key Info Grid - Compact Design */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="text-center p-4 bg-gradient-to-br from-brand-primary/5 to-purple-50 rounded-xl border border-brand-primary/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">{formatDuration(training.duration)}</div>
                    <div className="text-xs text-gray-600 font-medium">{t('training.duration')}</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-brand-secondary/5 to-pink-50 rounded-xl border border-brand-secondary/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">{formatTrainingLocation(training)}</div>
                    <div className="text-xs text-gray-600 font-medium">{t('training.location')}</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-brand-mint/5 to-teal-50 rounded-xl border border-brand-mint/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">
                      {training.sessions && training.sessions.length > 0
                        ? new Date(training.sessions[0].session_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })
                        : (training.schedule_date && !isNaN(new Date(training.schedule_date).getTime())
                            ? new Date(training.schedule_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })
                            : t('training.noSession'))
                      }
                    </div>
                    <div className="text-xs text-gray-600 font-medium">
                      {training.sessions && training.sessions.length > 1 
                        ? t('training.upcomingSessions', { count: training.sessions.length })
                        : t('training.date')
                      }
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-brand-accent/5 to-yellow-50 rounded-xl border border-brand-accent/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-accent to-yellow-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">
                      {training.language || training.languages || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">{t('training.language', 'Language')}</div>
                  </div>
                </div>

                {/* Modalités & Accès (compact) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="p-6 bg-gradient-to-br from-brand-primary/5 to-purple-50 border border-brand-primary/10 rounded-2xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      {t('training.terms')}
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2">
                      {training.format && <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.formatLabel')}: {training.format}</li>}
                      {training.language && <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.languageLabel')}: {training.language}</li>}
                      <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.certificateAndSupportIncluded')}</li>
                    </ul>
                  </div>
                  <div className="p-6 bg-gradient-to-br from-brand-secondary/5 to-pink-50 border border-brand-secondary/10 rounded-2xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      {t('training.accessDeadlines')}
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-secondary rounded-full"></span>{t('training.confirmationWithin48Hours')}</li>
                      {training.sessions && training.sessions.length > 0 ? (
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-brand-secondary rounded-full"></span>
                          {training.sessions.length === 1 
                            ? `${t('training.sessionDate')}: ${new Date(training.sessions[0].session_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}`
                            : `${training.sessions.length} ${t('training.sessionsAvailable')}`
                          }
                        </li>
                      ) : (
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-brand-secondary rounded-full"></span>
                          {training.schedule_date && !isNaN(new Date(training.schedule_date).getTime())
                            ? `${t('training.sessionDate')}: ${new Date(training.schedule_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}`
                            : t('training.noSession')}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Description Section - Collapsible */}
            <ScrollReveal delay={0.1}>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
              <button
                onClick={() => toggleSection('description')}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
              >
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-brand-primary" />
                  {t('training.description')}
                </h2>
                {expandedSections.description ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedSections.description && (
                <div
                  className="px-4 pb-4"
                >
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed">
                      {training.full_description || t('view.description.title')}
                    </p>
                  </div>
                </div>
              )}
              </div>
            </ScrollReveal>

            {/* Learning Objectives - Collapsible */}
                      {training.learning_objectives && (
              <ScrollReveal delay={0.2}>
                <div
                  className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6"
                >
                <button
                  onClick={() => toggleSection('objectives')}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                >
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Target className="h-5 w-5 text-emerald-600" />
                    {t('training.objectives.title')}
                  </h2>
                  {expandedSections.objectives ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedSections.objectives && (
                  <div className="px-4 pb-4">
                    <div className="prose prose-gray max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {training.learning_objectives}
                      </p>
                    </div>
                  </div>
                )}
                </div>
              </ScrollReveal>
            )}

            {/* Skills Gained - Collapsible */}
            {training.skills_gained && (
              <ScrollReveal delay={0.3}>
                <div
                  className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6"
                >
                <button
                  onClick={() => toggleSection('skills')}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                >
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                    {t('training.skills.title')}
                  </h2>
                  {expandedSections.skills ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedSections.skills && (
                  <div className="px-4 pb-4">
                    <div className="prose prose-gray max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {training.skills_gained}
                      </p>
                    </div>
                  </div>
                )}
                </div>
              </ScrollReveal>
            )}

            {/* Target Audience - Collapsible */}
            {training.target_audience && (
              <ScrollReveal delay={0.4}>
                <div
                  className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6"
                >
                <button
                  onClick={() => toggleSection('audience')}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                >
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-600" />
                    {t('training.audience.title')}
                  </h2>
                  {expandedSections.audience ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedSections.audience && (
                  <div className="px-4 pb-4">
                    <div className="prose prose-gray max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {training.target_audience}
                      </p>
                    </div>
                  </div>
                )}
                </div>
              </ScrollReveal>
            )}

            {/* Trainer Profile */}
            {training.trainer_profile_snippets && (
              <ScrollReveal delay={0.5}>
                <div
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
                >
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="h-6 w-6 text-emerald-600" />
                  {t('training.trainerProfile')}
                </h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {training.trainer_profile_snippets}
                  </p>
                </div>
                </div>
              </ScrollReveal>
            )}

            {/* Reviews Section */}
            <ScrollReveal delay={0.6}>
              <div
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
              >
              <div 
                className="flex items-center justify-between mb-8"
              >
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div 
                    className="w-10 h-10 bg-gradient-to-r from-brand-accent to-yellow-600 rounded-xl flex items-center justify-center"
                  >
                    <Star className="h-6 w-6 text-white" />
                  </div>
                  {t('training.reviewsRatings')}
                </h2>
                <motion.button
                  onClick={() => setShowReviewForm(true)}
                  className="px-6 py-3 bg-gradient-to-r from-brand-secondary to-pink-600 text-white rounded-xl hover:from-pink-600 hover:to-brand-secondary transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  variants={pulseVariants}
                  whileHover="hover"
                  whileTap="tap"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {t('training.leaveReview')}
                </motion.button>
              </div>

              {/* Rating Summary */}
              <div 
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
              >
                {/* Overall Rating */}
                <div 
                  className="text-center p-6 bg-gradient-to-br from-brand-accent/5 to-yellow-50 rounded-2xl border border-brand-accent/10"
                >
                  <div className="text-5xl font-bold bg-gradient-to-r from-brand-accent to-yellow-600 bg-clip-text text-transparent mb-3">
                    {getAverageRating()}
                  </div>
                  <div className="flex items-center justify-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-6 w-6 ${
                          star <= getAverageRating()
                            ? 'text-brand-accent fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {training?.ratingStats?.totalReviews || reviews.length} {t('training.reviews')}
                   </div>
                   {training?.viewCount && (
                    <div className="text-xs text-gray-500 mt-2">
                       {training.viewCount} {t('training.views')}
                     </div>
                   )}
                </div>

                {/* Rating Distribution */}
                <div className="md:col-span-2">
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const distribution = getRatingDistribution();
                      const count = distribution[rating as keyof typeof distribution];
                      const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                      
                      return (
                        <div key={rating} className="flex items-center gap-3">
                          <div className="flex items-center gap-1 w-8">
                            <span className="text-sm font-medium text-gray-600">{rating}</span>
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          </div>
                          <div className="flex-1 bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-brand-primary to-purple-600 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="w-12 text-right">
                            <span className="text-sm text-gray-600">{count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Review Filters */}
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="text-sm font-medium text-gray-700">{t('training.filterBy')}:</span>
                {[
                  { value: 'all', label: t('training.all') },
                  { value: '5', label: t('training.fiveStars') },
                  { value: '4', label: t('training.fourStars') },
                  { value: '3', label: t('training.threeStars') },
                  { value: '2', label: t('training.twoStars') },
                  { value: '1', label: t('training.oneStar') }
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setReviewFilter(filter.value as any)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      reviewFilter === filter.value
                        ? 'bg-gradient-to-r from-brand-primary to-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-brand-primary/10 hover:text-brand-primary border border-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Reviews List */}
              <div className="space-y-6">
                {getFilteredReviews().map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-6 last:border-b-0">
                    <div className="flex items-start gap-4">
                                             {/* User Avatar */}
                       <div className="flex-shrink-0">
                         <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                           <User className="h-5 w-5 text-emerald-600" />
                         </div>
                       </div>

                       {/* Review Content */}
                       <div className="flex-1">
                         <div className="flex items-center gap-2 mb-2">
                           <h4 className="font-semibold text-gray-900">
                             {review.user.firstName && review.user.lastName 
                               ? `${review.user.firstName} ${review.user.lastName}`
                               : review.user.username
                             }
                           </h4>
                           {review.is_verified && (
                             <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                               {t('common.verified')}
                             </span>
                           )}
                         </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= review.rating
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatReviewDate(review.created_at)}
                          </span>
                        </div>

                        {/* Comment */}
                        <p className="text-gray-700 leading-relaxed mb-3">
                          {review.comment}
                        </p>

                                                 {/* Actions */}
                         <div className="flex items-center gap-4">
                           <button 
                             onClick={() => handleMarkHelpful(review.id)}
                             className="flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 transition-colors"
                           >
                             <ThumbsUp className="h-4 w-4" />
                    {t('training.helpful')} ({review.helpful_count})
                           </button>
                         </div>
                      </div>
                    </div>
                  </div>
                ))}

                {getFilteredReviews().length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">{t('common.noResults')}</p>
                  </div>
                )}
              </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Pricing Card */}
            <div
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-6 sticky top-6"
            >
              <div className="text-center mb-8">
                <div className="text-5xl font-bold bg-gradient-to-r from-brand-primary to-purple-600 bg-clip-text text-transparent mb-3">
                  {formatPrice(training.pricing_dzd)}
                </div>
                <div className="text-sm text-gray-600 font-medium">{t('training.perParticipant')}</div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 bg-gradient-to-r from-brand-mint to-teal-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium">{t('training.certificateIncluded')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 bg-gradient-to-r from-brand-mint to-teal-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium">{t('training.courseMaterials')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 bg-gradient-to-r from-brand-mint to-teal-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium">{t('training.accessResources')}</span>
                </div>
              
              </div>

              {/* Error message */}
              {enrollmentError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <p className="text-sm text-red-700 font-medium">{enrollmentError}</p>
                  </div>
                </div>
              )}

              {/* Already enrolled badge */}
              {isEnrolled && !enrollmentError && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-sm text-green-700 font-medium">{t('training.alreadyEnrolled')}</p>
                  </div>
                </div>
              )}

              <motion.button 
                onClick={handleSubscribe}
                disabled={isEnrolled || enrollmentError !== null || !hasAvailableSessions()}
                className="w-full bg-gradient-to-r from-brand-primary to-purple-600 text-white py-4 px-6 rounded-xl font-bold hover:from-purple-600 hover:to-brand-primary transition-all duration-200 mb-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                variants={pulseVariants}
                whileHover="hover"
                whileTap="tap"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {isEnrolled 
                  ? t('training.alreadyEnrolled') 
                  : !hasAvailableSessions() 
                    ? t('training.subscriptionsNotAvailable')
                    : isAuthenticated 
                      ? t('training.registerNow') 
                      : t('training.loginToRegister')
                }
              </motion.button>

            </div>

            {/* Domaine Info */}
            {training.domaine && (
              <div
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  {t('training.trainingDomain')}
                </h3>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-brand-primary/5 to-purple-50 rounded-xl border border-brand-primary/10">
                    <h4 className="font-bold text-gray-900 mb-2 text-lg">
                      {training.domaine.name_french || training.domaine.name || t('training.domainNotSpecified')}
                    </h4>
                    <p className="text-sm text-gray-600 font-medium">{t('training.mainSpecialization')}</p>
                  </div>

                  {training.domaine.name_arabic && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center">
                        <Award className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-700">{training.domaine.name_arabic}</span>
                    </div>
                  )}

                  {training.domaine.name_english && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center">
                        <Award className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-700">{training.domaine.name_english}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Training Center Info */}
            <div
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-xl flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                {t('public.trainingCenters.title')}
              </h3>
              
              <div className="space-y-6">
                <div className="p-4 bg-gradient-to-br from-brand-secondary/5 to-pink-50 rounded-xl border border-brand-secondary/10">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-3">
                    {training.training_center?.logo ? (
                      <img
                        src={training.training_center.logo.startsWith('http') ? training.training_center.logo : `${API_ENDPOINTS.UPLOADS}/${training.training_center.logo}`}
                        alt={`${training.training_center?.name} Logo`}
                        className="w-32 h-32 sm:w-40 sm:h-40 lg:w-44 lg:h-44 rounded-2xl object-cover border-2 border-gray-200 shadow-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-32 h-32 sm:w-40 sm:h-40 lg:w-44 lg:h-44 rounded-2xl bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center shadow-lg border-2 border-gray-200"
                      style={{ display: training.training_center?.logo ? 'none' : 'flex' }}
                    >
                      <Building2 className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 text-white" />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h4 className="font-bold text-gray-900 mb-1 text-lg sm:text-xl">
                        {training.training_center?.name}
                      </h4>
                      <p className="text-sm text-gray-600 font-medium">{formatTrainingCenterLocation(training.training_center)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center">
                    <Globe className="h-4 w-4 text-white" />
                </div>
                  <span className="font-medium text-gray-700">{t('training.accreditedCenter')}</span>
                </div>

                 

                <div className="pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-4 text-sm">
                    <button className="flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors font-medium">
                      <Phone className="h-4 w-4" />
                      {t('training.call')}
                    </button>
                    <button className="flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors font-medium">
                      <Mail className="h-4 w-4" />
                      {t('training.email')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Schedule Dates - Only show if no sessions are available */}
            {training.schedule_dates && (!training.sessions || training.sessions.length === 0) && (
              <div
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-emerald-600" />
                  {t('training.otherSessions')}
                </h3>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">
                    {training.schedule_dates}
                  </p>
                </div>
              </div>
            )}

                         {/* Detailed Program PDF */}
             {training.detailed_program_pdf && (
               <div
                 className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
               >
                 <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                   <FileText className="h-5 w-5 text-emerald-600" />
                   {t('training.detailedProgram')}
                 </h3>
                 <a
                   href={`${API_ENDPOINTS.UPLOADS}/${training.detailed_program_pdf}`}
                   target="_blank"
                   rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 w-full justify-center px-4 py-3 border border-emerald-600 text-emerald-600 font-semibold rounded-lg hover:bg-emerald-50 transition-colors"
                   onClick={(e) => {
                     // Check if file exists before opening
                     fetch(`${API_ENDPOINTS.UPLOADS}/${training.detailed_program_pdf}`)
                       .then(response => {
                         if (!response.ok) {
                           e.preventDefault();
                           alert(t('training.pdfNotAvailable'));
                         }
                       })
                       .catch(() => {
                         e.preventDefault();
                         alert(t('training.pdfNotAvailable'));
                       });
                   }}
                 >
                   <Download className="h-4 w-4" />
                   {t('training.downloadPDF')}
                   <ExternalLink className="h-4 w-4" />
                 </a>
               </div>
             )}
          </div>
        </div>
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">{t('training.leaveReview')}</h3>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('training.yourRating')}
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setSelectedRating(star)}
                        className={`text-3xl transition-colors ${
                          star <= selectedRating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'
                        }`}
                      >
                        <Star className={`h-8 w-8 ${star <= selectedRating ? 'fill-current' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('training.yourReview')}
                  </label>
                  <textarea
                    id="comment"
                    rows={4}
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t('home.contact.message')}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowReviewForm(false)}
                    disabled={isSubmittingReview}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    onClick={handleSubmitReview}
                    disabled={isSubmittingReview || selectedRating === 0 || !reviewComment.trim()}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-primary to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-brand-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:transform-none"
                  >
                    {isSubmittingReview ? t('training.publishing') : t('training.publishReview')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Form Modal */}
      {showSubscriptionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('subscriptionForm.title')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('subscriptionForm.confidentialityNote')}
                </p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subscriptionForm.fields.currentPosition')}
                    </label>
                    <input
                      type="text"
                      value={formData.current_position}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_position: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder={t('subscriptionForm.fields.currentPositionPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subscriptionForm.fields.companyName')}
                    </label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder={t('subscriptionForm.fields.companyNamePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subscriptionForm.fields.department')}
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder={t('subscriptionForm.fields.departmentPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subscriptionForm.fields.yearsOfExperience')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={formData.years_of_experience}
                      onChange={(e) => setFormData(prev => ({ ...prev, years_of_experience: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder={t('subscriptionForm.fields.yearsOfExperiencePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    {t('subscriptionForm.fields.specialAccommodation')}
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="has_special_accommodation"
                        checked={formData.has_special_accommodation === true}
                        onChange={() => setFormData(prev => ({ ...prev, has_special_accommodation: true }))}
                        className="w-4 h-4 text-brand-primary focus:ring-brand-primary"
                      />
                      <span className="text-gray-700">{t('subscriptionForm.fields.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="has_special_accommodation"
                        checked={formData.has_special_accommodation === false}
                        onChange={() => setFormData(prev => ({ ...prev, has_special_accommodation: false }))}
                        className="w-4 h-4 text-brand-primary focus:ring-brand-primary"
                      />
                      <span className="text-gray-700">{t('subscriptionForm.fields.no')}</span>
                    </label>
                  </div>
                </div>

                {formData.has_special_accommodation && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subscriptionForm.fields.specialAccommodationDetails')}
                    </label>
                    <textarea
                      value={formData.special_accommodation_details}
                      onChange={(e) => setFormData(prev => ({ ...prev, special_accommodation_details: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder={t('subscriptionForm.fields.specialAccommodationDetailsPlaceholder')}
                    />
                  </div>
                )}

                <div className="flex gap-4 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowSubscriptionForm(false)}
                    className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    {t('subscriptionForm.buttons.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-brand-primary to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-brand-primary transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    {t('training.continueToSessions')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDetailPage;
